import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './style.scss';

interface NotificationPopupProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: React.ReactNode;
  /** Texto do botão de ação. */
  actionLabel: string;
  /** Rota para onde o botão de ação navega. */
  actionPath: string;
  /** Tempo em ms até o popup fechar automaticamente. `null` desativa o
   *  auto-close. Padrão: 8000 (8s). */
  autoCloseMs?: number | null;
}

/**
 * Pop-up fixado no canto inferior direito da tela, com uma notificação e um
 * botão de ação. Visual herdado dos balões `border-pink-bb-2` usados nos
 * fluxos de inscrição/votação, com botão CTA amarelo no padrão da home.
 *
 * Fecha sozinho após `autoCloseMs` (default 8s).
 */
export const NotificationPopup: React.FC<NotificationPopupProps> = ({
  open,
  onClose,
  title,
  description,
  actionLabel,
  actionPath,
  autoCloseMs = 8000,
}) => {
  const navigate = useNavigate();

  // Auto-close: dispara apenas enquanto o popup está aberto e o timer for um
  // número positivo. Se o usuário fechar manualmente antes disso, o cleanup
  // limpa o timer.
  useEffect(() => {
    if (!open || autoCloseMs === null || autoCloseMs <= 0) {
      return;
    }
    const timeoutId = window.setTimeout(onClose, autoCloseMs);
    return () => window.clearTimeout(timeoutId);
  }, [open, autoCloseMs, onClose]);

  if (!open) {
    return null;
  }

  const handleAction = () => {
    onClose();
    navigate(actionPath);
  };

  return (
    <div
      className="notification-popup"
      role="dialog"
      aria-live="polite"
      aria-labelledby="notification-popup-title"
    >
      <div className="notification-popup__header">
        <h3 className="notification-popup__title" id="notification-popup-title" tabIndex={0}>
          {title}
        </h3>
      </div>

      <div className="notification-popup__body">
        <p className="notification-popup__description" tabIndex={0}>
          {description}
        </p>
      </div>

      <div className="notification-popup__footer">
        <button
          type="button"
          className="notification-popup__btn notification-popup__btn--secondary"
          onClick={onClose}
        >
          FECHAR
        </button>
        <button
          type="button"
          className="notification-popup__btn notification-popup__btn--primary"
          onClick={handleAction}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
};
